import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

// In production, default to same-origin "/api" (web server proxies to backend).
// Can be overridden via REACT_APP_API_URL.
const API_BASE_URL = getApiBaseUrl();
 

// Log API base URL for debugging
console.log('API Base URL configured:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Log successful responses for debugging
    console.log('API Response:', {
      url: response.config.url,
      method: response.config.method,
      baseURL: response.config.baseURL,
      status: response.status,
      data: response.data,
      dataType: typeof response.data,
      contentType: response.headers['content-type']
    });
    
    // Check if response is HTML (should be JSON)
    if (response.headers['content-type'] && 
        response.headers['content-type'].includes('text/html')) {
      console.warn('Warning: API returned HTML instead of JSON. This usually means the API URL is incorrect or being proxied incorrectly.');
    }
    
    return response;
  },
  (error) => {
    // Log error responses for debugging
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      baseURL: error.config?.baseURL,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      dataType: typeof error.response?.data,
      contentType: error.response?.headers['content-type']
    });
    
    // Handle network errors (no response from server)
    if (error.code === 'ERR_NETWORK' || !error.response) {
      console.error('Network Error: Backend server is not reachable. Please ensure:');
      console.error(`1. Backend server is running on ${API_BASE_URL}`);
      console.error('2. Check if the backend server is started: cd backend-node && npm start');
      console.error('3. Verify the API URL is correct in your .env file');
      console.error('4. If using serve -s build, use npm run build:test instead of npm run build');
    }
    
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  verify: () => api.get('/auth/verify'),
};

// Blog API
export const blogAPI = {
  getAll: (limit) => api.get('/blog', { params: { limit } }),
  getAllAdmin: () => api.get('/blog/all'),
  getById: (id) => api.get(`/blog/${id}`),
  create: (data) => api.post('/blog', data),
  update: (id, data) => api.put(`/blog/${id}`, data),
  delete: (id) => api.delete(`/blog/${id}`),
};

// Upload API
export const uploadAPI = {
  uploadImage: (formData) => {
    const token = localStorage.getItem('adminToken');
    return axios.post(`${API_BASE_URL}/upload/image`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteImage: (filename) => api.delete(`/upload/image/${filename}`),
};

export default api;
